import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Mail, Lock, AlertCircle } from 'lucide-react';
import { loginUser } from '../../services/firebase/auth';
import { logActivity } from '../../services/firebase/firestore';
import { ActionType, EntityType } from '../../types/deal.types';

const schema = yup.object({
  email: yup.string().email('Invalid email').required('Email is required'),
  password: yup.string().required('Password is required')
});

type FormData = yup.InferType<typeof schema>;

export const LoginFormPasskey: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema)
  });

  const location = useLocation();
  const from = (location.state as { from?: { pathname?: string; search?: string } } | null)?.from;
  const redirectTo =
    from?.pathname && from.pathname !== '/login'
      ? `${from.pathname}${from.search || ''}`
      : '/dashboard';

  const onSubmit = async (data: FormData) => {
    setIsLoading(true);
    setError(null);

    try {
      const user = await loginUser(data.email, data.password);

      await logActivity(
        user.uid,
        data.email,
        ActionType.LOGIN,
        EntityType.USER,
        user.uid,
        data.email,
        undefined,
        'success'
      );

      // Honour the deep link the user arrived with (ProtectedRoute stores it
      // as state.from) — e.g. /my-tickets?ticket=… from a support email.
      // Without this, every email link bounced to the dashboard after login.
      navigate(redirectTo, { replace: true });
    } catch (err: any) {
      setIsLoading(false);
      if (err.code === 'auth/user-not-found') {
        setError('No account found with this email address.');
      } else if (err.code === 'auth/wrong-password') {
        setError('Incorrect password. Please try again.');
      } else if (err.code === 'auth/invalid-email') {
        setError('Invalid email address format.');
      } else if (err.code === 'auth/invalid-credential') {
        setError('Invalid email or password. Please check your credentials.');
      } else {
        setError('Failed to sign in. Please try again.');
      }
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/10 to-secondary/10 p-4">
      <div className="w-full max-w-md">
        <div className="glass-card p-8 space-y-6">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary to-primary-dark rounded-full mb-4">
              <span className="text-white text-2xl font-bold">PC</span>
            </div>
            {import.meta.env.VITE_ENV_NAME === 'DEVELOPMENT' && (
              <div className="mb-4 px-3 py-1 bg-orange-500 text-white text-xs font-bold rounded-full inline-block">
                DEV: {import.meta.env.VITE_FIREBASE_PROJECT_ID}
              </div>
            )}
            <h1 className="text-2xl font-bold text-neutral-900">PropCloudAI</h1>
            <p className="text-neutral-600 mt-2">Sign in to your account</p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 w-5 h-5" />
                <input
                  {...register('email')}
                  type="email"
                  className="input-field pl-10"
                  placeholder="Enter your email"
                  disabled={isLoading}
                  autoComplete="username"
                />
              </div>
              {errors.email && (
                <p className="text-error text-sm mt-1">{errors.email.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 w-5 h-5" />
                <input
                  {...register('password')}
                  type="password"
                  className="input-field pl-10"
                  placeholder="Enter your password"
                  disabled={isLoading}
                  autoComplete="current-password"
                />
              </div>
              {errors.password && (
                <p className="text-error text-sm mt-1">{errors.password.message}</p>
              )}
            </div>

            {error && (
              <div className="bg-error/10 border border-error/20 rounded-lg p-3 flex items-start space-x-2">
                <AlertCircle className="w-5 h-5 text-error flex-shrink-0 mt-0.5" />
                <p className="text-sm text-error">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="primary-button w-full py-3"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                </div>
              ) : (
                'Sign In'
              )}
            </button>

            <div className="text-center">
              <Link
                to="/forgot-password"
                className="text-sm text-primary hover:text-primary-dark transition-colors"
              >
                Forgot Password?
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};